const COMPANY_DETAILS = {
  name: 'VEDANT INFO EDGE INDIA LLP',
  gstin: '27ABBFV5785B1Z0',
  cin: 'ACV-0022',
  logo: '/finallogo.png',
  website: 'www.360coordinates.com',
  email: 'info@360coordinates.com',
  address:
    'SR NO 62/3/4 TF OFFICE NO 22 ASTONPLAZA AMBEGAON BK KATRAJ POLICE STATION HAVELI Pune, Pune, Maharashtra, India - 411046',
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(Number(amount) || 0);

const formatInvoiceDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString)
    .toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
    .replace(/\b(am|pm)\b/g, (match) => match.toUpperCase());
};

const formatInvoiceNumber = (id) => String(Math.max(Number(id) || 1, 1)).padStart(6, '0');

const formatDateOnly = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  return String(value);
};

const formatUserType = (userType) => {
  const type = String(userType || '').toLowerCase();
  if (!type) return 'N/A';
  if (type === 'seller') return 'Seller';
  if (type === 'agent') return 'Agent';
  if (type === 'buyer') return 'Buyer';
  return userType;
};

const getTaxTypeLabel = (subscription) => {
  if (subscription.tax_type === 'cgst_sgst') return 'CGST 9% + SGST 9%';
  if (subscription.tax_type === 'igst') return 'IGST 18%';
  return subscription.tax_type ? String(subscription.tax_type).toUpperCase() : 'IGST 18%';
};

const buildDetailRow = (label, value) => `
  <div class="detail-row">
    <div class="detail-label">${escapeHtml(label)}</div>
    <div class="detail-value">${escapeHtml(displayValue(value))}</div>
  </div>
`;

const getTaxRows = (subscription) => {
  if (subscription.tax_type === 'cgst_sgst') {
    return [
      { label: 'CGST @ 9%', amount: subscription.cgst_rupees || 0 },
      { label: 'SGST @ 9%', amount: subscription.sgst_rupees || 0 },
    ];
  }

  return [{ label: 'IGST @ 18%', amount: subscription.igst_rupees || subscription.gst_rupees || 0 }];
};

const numberToWords = (amount) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const twoDigits = (num) => {
    if (num < 20) return ones[num];
    return `${tens[Math.floor(num / 10)]}${num % 10 ? ` ${ones[num % 10]}` : ''}`.trim();
  };

  const threeDigits = (num) => {
    const hundred = Math.floor(num / 100);
    const rest = num % 100;
    return `${hundred ? `${ones[hundred]} Hundred ` : ''}${rest ? twoDigits(rest) : ''}`.trim();
  };

  const toIndianWords = (num) => {
    if (num === 0) return 'Zero';
    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const hundred = num % 1000;
    const parts = [];

    if (crore) parts.push(`${twoDigits(crore)} Crore`);
    if (lakh) parts.push(`${twoDigits(lakh)} Lakh`);
    if (thousand) parts.push(`${twoDigits(thousand)} Thousand`);
    if (hundred) parts.push(threeDigits(hundred));

    return parts.join(' ').trim();
  };

  return `${toIndianWords(Math.round(Number(amount || 0)))} INR Only /-`;
};

/**
 * Open printable invoice (same layout as Seller My Plans receipt).
 * @param {object} subscription - invoice line items (plan_name, amounts, dates, etc.)
 * @param {object} billingProfile - { full_name, phone, email, address, ... }
 */
export const downloadSubscriptionInvoice = (subscription, billingProfile = {}) => {
  const invoiceNumber = formatInvoiceNumber(subscription.id);
  const billingName =
    billingProfile.full_name ||
    billingProfile.name ||
    subscription.user_name ||
    'Customer';
  const billingPhone =
    billingProfile.phone ||
    subscription.user_phone ||
    'N/A';
  const billingEmail =
    billingProfile.email ||
    subscription.user_email ||
    'N/A';
  const customerGst =
    subscription.gst_number ||
    billingProfile.gst_number ||
    'N/A';
  const billingAddress =
    [
      billingProfile.address,
      billingProfile.additional_address,
      billingProfile.location,
      billingProfile.city,
      billingProfile.state,
      billingProfile.pincode,
    ]
      .filter(Boolean)
      .join(', ') || subscription.place_of_supply || 'Address not provided';

  const planName = (subscription.plan_name || subscription.plan_code || 'Plan').toUpperCase();
  const durationMonths = Number(subscription.duration_months) || 1;
  const validFrom =
    subscription.starts_at ||
    subscription.start_date ||
    subscription.created_at;
  const validTo = subscription.ends_at || subscription.end_date || 'N/A';
  const userType = formatUserType(subscription.user_type || billingProfile.user_type);

  const customerDetailsHtml = `
    <div class="details-section">
      <div class="muted box-title">CUSTOMER DETAILS</div>
      <div class="details-grid">
        ${buildDetailRow('Customer Name', billingName)}
        ${buildDetailRow('Mobile Number', billingPhone)}
        ${buildDetailRow('Email Address', billingEmail)}
        ${buildDetailRow('Customer GSTIN', customerGst)}
        ${buildDetailRow('Account Type', userType)}
        ${buildDetailRow('Billing Address', billingAddress)}
      </div>
    </div>
  `;

  const paymentDetailsHtml = `
    <div class="details-section">
      <div class="muted box-title">PAYMENT DETAILS</div>
      <div class="details-grid">
        ${buildDetailRow('Payment Date', formatInvoiceDate(subscription.created_at))}
        ${buildDetailRow('Valid From', formatDateOnly(validFrom))}
        ${buildDetailRow('Valid Till', formatDateOnly(validTo))}
        ${buildDetailRow('Tax Type', getTaxTypeLabel(subscription))}
        ${buildDetailRow('HSN / SAC', '9972')}
      </div>
    </div>
  `;

  const taxRowsHtml = getTaxRows(subscription)
    .map(
      (taxRow) => `
          <tr>
            <td class="tax-row-label">${escapeHtml(taxRow.label)}</td>
            <td class="tax-row-value">${escapeHtml(formatCurrency(taxRow.amount))}</td>
          </tr>
        `
    )
    .join('');

  const termsHtml = `
      <div class="terms-section">
        <div class="muted box-title">TERMS & CONDITIONS</div>
        <div class="terms-list">
          <div class="terms-item"><strong>1. Service Description</strong><br />The invoice is issued for property listing, promotion, advertisement, or related digital services provided on the website/platform.</div>
          <div class="terms-item"><strong>2. No Refund Policy</strong><br />Payments once made are non-refundable, under any circumstances.<br />No refund for unused listing period or early termination.</div>
          <div class="terms-item"><strong>3. Service Validity</strong><br />Property listings or promotional services will remain active for the agreed duration only.<br />Renewal requires fresh payment.</div>
          <div class="terms-item"><strong>4. Client Responsibilities</strong><br />Client must provide accurate property details, images, and legal information.<br />The company is not responsible for incorrect or misleading data provided by the client.</div>
          <div class="terms-item"><strong>5. Content & Legal Compliance</strong><br />Client confirms that the property is legally owned/authorized for listing.<br />Any disputes related to property legality are the sole responsibility of the client.</div>
          <div class="terms-item"><strong>6. Account Suspension</strong><br />The company reserves the right to suspend/remove listings for non-payment, fake or illegal property listings, or violation of platform policies.</div>
          <div class="terms-item"><strong>7. Limitation of Liability</strong><br />The company is not liable for any loss, damages, or failed transactions arising from property deals.<br />The platform acts only as a listing/advertising medium.</div>
          <div class="terms-item"><strong>8. Cancellation Policy</strong><br />Once the service is activated, cancellation is not allowed.<br />Any cancellation request before activation is subject to approval.</div>
          <div class="terms-item"><strong>9. Jurisdiction</strong><br />All disputes shall be subject to the jurisdiction of Pune, Maharashtra courts.</div>
          <div class="terms-item"><strong>10. Force Majeure</strong><br />The company shall not be liable for failure due to events beyond control (server downtime, natural disasters, etc.).</div>
          <div class="terms-item"><strong>11. Pricing Policy </strong><br />Prices are subject to change without prior notice.</div>
          <div class="terms-item"><strong>12. Amendments</strong><br />Terms & conditions may be updated without prior notice.</div>
        </div>
      </div>
    `;

  const invoiceHtml = `
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8" />
          <title></title>
          <style>
            @page { size: A4; margin: 10mm;border: 1px solid rgba(0, 0, 0, 0.35);  }
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; background: #f8fafc; color: #0f172a; margin: 0; padding: 10px;  }
            .invoice { width: 190mm; max-width: 190mm; margin: 0 auto; background: #ffffff; padding: 14mm; padding-bottom: 34mm;  }
            .top { display:block; margin-bottom:14px; }
            .company { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; min-width: 0; }
            .company-details { flex:1; min-width:0; text-align:right; }
            .company h1 { margin:0; font-size:22px; line-height:1.2; }
            .company-logo { width: 118px; height: auto;margin-top:-20px; display:block; flex-shrink:0; }
            .muted { color:#64748b; font-size:12px; line-height:1.35; }
            .invoice-title { margin-top: 12px; font-size: 22px; font-weight: 700; }
            .invoice-subtitle { margin-top: 6px; }
            .grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:14px; }
            .box { border-top:1px solid rgba(0, 0, 0, 0.35); padding-top:10px; min-height: 86px; }
            .box-title { font-weight:700; letter-spacing:0.04em; text-transform:uppercase; }
            .box-content { margin-top:8px; }
            .box-content-strong { margin-top:8px; font-weight:700; }
            .date-grid { margin-top: 0; }
            .details-section { margin-bottom: 14px; border-top: 1px solid rgba(0, 0, 0, 0.35); padding-top: 10px; }
            .details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 20px; margin-top: 8px; }
            .detail-row { font-size: 12px; line-height: 1.4; }
            .detail-label { color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; font-size: 10px; }
            .detail-value { color: #0f172a; margin-top: 3px; word-break: break-word; }
            table { width:100%; border-collapse:collapse; margin-top:6px; }
            th { background:#f8fafc; color:#475569; font-size:12px; text-align:left; padding:7px 8px; }
            td { padding:7px 8px; border-bottom:1px solid rgba(0, 0, 0, 0.35); vertical-align:top; font-size:12px; line-height:1.35; }
            .right { text-align:right; }
            .description-cell { width: 52%; }
            .line-description-title { font-weight:700; }
            .line-description-text { margin-top:6px; }
            .tax-row-label { color:#475569; }
            .tax-row-value { text-align:right; color:#0f172a; }
            .taxname{font-weight:700;}
            .totals { width:280px; margin-left:auto; margin-top:12px; }
            .totals td { padding:5px 0; border-bottom:none; font-size:12px; }
            .total-amount td { font-size:20px; font-weight:700; }
            .terms-section { margin-top:18px; padding-top:10px; border-top:1px solid rgba(0, 0, 0, 0.35); page-break-before: always; break-before: page; }
            .terms-list { margin-top:8px; }
            .terms-item { font-size:11px; line-height:1.45; color:#334155; margin-bottom:9px; }
            .print-footer { border-top:1px solid rgba(0, 0, 0, 0.35); padding-top:8px; margin-top:18px; text-align:center; }
            @media print {
              body { background:#fff; padding:0; }
              .invoice { width: 100%; max-width: 100%; margin: 0; padding: 12mm 12mm 24mm 12mm;  }
              .print-footer {
                position: fixed;
                left: 12mm;
                right: 12mm;
                bottom: 8mm;
                background: #fff;
              }
            }
          </style>
        </head>
        <body>
          <div class="invoice">
            <div class="top">
              <div class="company">
                <img src="${escapeHtml(COMPANY_DETAILS.logo)}" alt="360Coordinates Logo" class="company-logo" />
                <div class="company-details">
                  <h1>${escapeHtml(COMPANY_DETAILS.name)}</h1>
                  <div class="muted">GSTIN - ${escapeHtml(COMPANY_DETAILS.gstin)}</div>
                  <div class="muted">CIN - ${escapeHtml(COMPANY_DETAILS.cin)}</div>
                  <div class="muted">${escapeHtml(COMPANY_DETAILS.website)}</div>
                  <div class="muted">${escapeHtml(COMPANY_DETAILS.email)}</div>
                </div>
              </div>
              <div class="invoice-title">Invoice # ${escapeHtml(invoiceNumber)}</div>
              <div class="muted invoice-subtitle">Subscription invoice for property display plan</div>
            </div>

            ${customerDetailsHtml}
            ${paymentDetailsHtml}

            <table>
              <thead>
                <tr>
                  <th>DESCRIPTION</th>
                  <th class="right">RATE/ITEM</th>
                  <th class="right">QTY</th>
                  <th class="right">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="description-cell">
                    <div class="line-description-title">PROPERTY FOR DISPLAY</div>
                    <div class="muted line-description-text">
                      ${escapeHtml(planName)} FOR ${escapeHtml(String(durationMonths))} MONTH${durationMonths > 1 ? 'S' : ''}<br />
                      SAC  CODE :  9972
                    </div>
                  </td>
                  <td class="right">${escapeHtml(formatCurrency(subscription.base_rupees))}</td>
                  <td class="right">${escapeHtml(String(subscription.properties_limit || 1))}</td>
                  <td class="right">${escapeHtml(formatCurrency(subscription.base_rupees))}</td>
                </tr>
              </tbody>
            </table>

            <table class="totals">
              <tr>
                <td>Taxable Amount</td>
                <td class="right">${escapeHtml(formatCurrency(subscription.base_rupees))}</td>
              </tr>
              <tr>
                <td class="right taxname">${taxRowsHtml}</td>
              </tr>
              ${subscription.tax_type === 'cgst_sgst'
                ? `
              <tr>
                <td>Total GST</td>
                <td class="right">${escapeHtml(formatCurrency((subscription.cgst_rupees || 0) + (subscription.sgst_rupees || 0)))}</td>
              </tr>`
                : `
              <tr>
                <td>Total GST</td>
                <td class="right">${escapeHtml(formatCurrency(subscription.igst_rupees || subscription.gst_rupees || 0))}</td>
              </tr>`}
              <tr class="total-amount">
                <td>Total Amount Paid</td>
                <td class="right">${escapeHtml(formatCurrency(subscription.amount_rupees))}</td>
              </tr>
              <tr>
                <td colspan="2" class="right muted">(${escapeHtml(numberToWords(subscription.amount_rupees))})</td>
              </tr>
            </table>

            ${termsHtml}

            <div class="print-footer">
              <div class="box-content-strong">${escapeHtml(COMPANY_DETAILS.name)}</div>
              <div class="muted box-content">${escapeHtml(COMPANY_DETAILS.address)}</div>
            </div>
          </div>
          <script>
            window.onload = function () {
              document.title = '';
              window.print();
            };
          </script>
        </body>
      </html>
    `;

  const printWindow = window.open('', '_blank', 'width=1024,height=768');
  if (!printWindow) {
    window.alert('Please allow pop-ups to download the invoice.');
    return;
  }

  printWindow.document.open();
  printWindow.document.write(invoiceHtml);
  printWindow.document.close();
};

export const isPaidSubscription = (subscription) => {
  const amount = Number(subscription?.amount_rupees ?? subscription?.amount_paid ?? 0);
  const planType = String(subscription?.plan_type || subscription?.plan_code || '').toLowerCase();
  return amount > 0 && planType !== 'free';
};
